import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { GraduationCap, CheckCircle, Users, TrendingUp, BookOpen, Award } from 'lucide-react';

const Teachers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What should teachers include in their performance review?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Include student achievement data (test scores, growth metrics), classroom management successes, curriculum development, parent engagement initiatives, professional development completed, and extracurricular contributions. Quantify with specific numbers: 'Improved class average reading scores from 72% to 89% proficiency.'"
        }
      },
      {
        "@type": "Question",
        "name": "How do teachers measure their impact for evaluations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Track student growth percentiles, standardized test improvements, assignment completion rates, behavior referral reductions, parent satisfaction surveys, and student engagement metrics. Also document differentiated instruction successes and students who exceeded expectations."
        }
      },
      {
        "@type": "Question",
        "name": "What achievements beyond test scores should teachers document?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document club sponsorships, committee leadership, curriculum writing, peer mentoring, innovative teaching methods, technology integration, student awards won, parent communication improvements, and community partnerships developed. These show impact beyond standardized assessments."
        }
      },
      {
        "@type": "Question",
        "name": "How often should teachers track their accomplishments?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Weekly tracking is ideal. Note student breakthroughs, successful lessons, parent feedback, and professional development as they happen. Use a tool like BragBox to capture wins quickly between classes. Waiting until evaluation season means forgetting impactful moments."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Performance Review Tips for Teachers | BragBox</title>
        <meta name="description" content="Ace your teaching performance review with actionable tips. Learn how to document student growth, showcase classroom achievements, and present your impact beyond test scores." />
        <meta name="keywords" content="teacher performance review, educator self-evaluation, teaching achievements, student growth metrics, classroom impact, teacher career growth" />
        <link rel="canonical" href="https://bragbox.ai/tips/teachers" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#1F2A44] via-[#2d3a5c] to-[#1F2A44]">
        {/* Header */}
        <header className="bg-[#1F2A44]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                <span className="text-[#1F2A44] font-bold text-sm">B</span>
              </div>
              <span className="text-white font-semibold text-lg">BragBox</span>
            </Link>
            <Link
              to="/login?signup=true"
              className="bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C4A35A]/20 text-[#C4A35A] px-4 py-2 rounded-full text-sm mb-6">
              <GraduationCap className="w-4 h-4" />
              Education Career Tips
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Performance Review Tips for Teachers
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              You're shaping futures every day, but evaluation forms rarely capture the full picture. Learn how to document your classroom impact, student breakthroughs, and the countless ways you go beyond the curriculum.
            </p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-12 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Why Teachers Struggle with Self-Evaluations</h2>
            <p className="text-gray-300 mb-6">
              You helped a struggling reader finally click with phonics. You spent your lunch period talking a student through anxiety about an upcoming test. You redesigned your entire unit on fractions because you noticed half the class was lost. But when your principal asks for your self-evaluation, test scores dominate the conversation.
            </p>
            <p className="text-gray-300">
              Teaching impact is often invisible precisely because it's relational. The student who comes back five years later to thank you won't be captured in this year's data. The parent who finally started reading with their child at home isn't in your metrics. Your best work often leaves no paper trail.
            </p>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">10 Actionable Tips for Your Teaching Evaluation</h2>
            
            <div className="space-y-8">
              {/* Tip 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">1. Track Student Growth, Not Just Achievement</h3>
                    <p className="text-gray-300 mb-3">
                      A student who moved from 40% to 70% shows more impact than one who stayed at 95%. Document growth percentiles, individual progress, and students who exceeded their own expectations.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"84% of students showed measurable growth in reading comprehension, with 6 students moving from 'below grade level' to 'at grade level' proficiency. Class average grew from 68% to 82% on quarterly assessments."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2. Document Curriculum Innovation</h3>
                    <p className="text-gray-300 mb-3">
                      Did you create new units, adapt materials for diverse learners, or integrate technology in meaningful ways? That's intellectual work that benefits students for years.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Developed project-based learning unit on local ecosystems now adopted by three grade-level colleagues. Student engagement surveys showed 45% increase in reported interest in science."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3. Highlight Differentiated Instruction Successes</h3>
                    <p className="text-gray-300 mb-3">
                      Meeting diverse learner needs is sophisticated professional work. Document how you adapted instruction for ELL students, IEP accommodations, or gifted learners.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Implemented tiered reading groups serving 8 ELL students, 4 students with IEPs, and 5 advanced readers. All ELL students improved at least one reading level; IEP goal achievement rate reached 92%."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">4. Track Parent and Family Engagement</h3>
                    <p className="text-gray-300 mb-3">
                      Strong home-school connections improve outcomes. Document communication frequency, conference attendance, and parent feedback.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Maintained weekly parent newsletter with 78% open rate. Achieved 94% parent conference attendance (up from 71% previous year). Received 15 positive parent emails specifically noting communication quality."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">5. Document Student Awards and Achievements</h3>
                    <p className="text-gray-300 mb-3">
                      When your students win spelling bees, science fairs, or writing contests, that reflects your teaching. Track external recognition your students receive.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Three students placed in district science fair (1st, 2nd, and honorable mention). Two students published in district-wide literary magazine. Class won school recycling competition for 4 consecutive months."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 6 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">6. Capture Classroom Management Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      A well-managed classroom enables learning. Track behavior referral reductions, positive behavior systems, and restorative practices you've implemented.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Implemented morning meeting routine reducing transition time by 8 minutes daily. Office referrals decreased 60% compared to previous year. Zero suspensions in a class with 3 students on behavior intervention plans."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 7 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">7</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">7. Note Professional Development and Growth</h3>
                    <p className="text-gray-300 mb-3">
                      Workshops attended, certifications earned, and new skills implemented show commitment to growth. Document what you learned AND how you applied it.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Completed 45 PD hours including Google Certified Educator Level 1. Applied new assessment strategies from workshop, reducing grading time 30% while providing more detailed feedback."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 8 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">8</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">8. Document Extracurricular Contributions</h3>
                    <p className="text-gray-300 mb-3">
                      Club sponsorships, tutoring programs, coaching, and after-school activities show school citizenship beyond your contracted hours.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Sponsored robotics club with 22 members, leading team to regional competition. Volunteered 40 hours for after-school tutoring program serving 12 struggling students; 9 improved to passing grades."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 9 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">9</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">9. Highlight Peer Mentorship and Collaboration</h3>
                    <p className="text-gray-300 mb-3">
                      Supporting colleagues, mentoring new teachers, and leading PLCs demonstrate leadership. Document your contributions to the professional community.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Mentored 2 first-year teachers, meeting weekly throughout the year. Both received satisfactory evaluations and returned for second year. Led grade-level PLC focused on math intervention strategies."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 10 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">10</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">10. Build Your Brag Document Throughout the Year</h3>
                    <p className="text-gray-300 mb-3">
                      Don't wait until evaluation season to remember your wins. Capture student breakthroughs, parent thank-yous, and successful lessons as they happen.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Pro Tip:</p>
                      <p className="text-gray-300 text-sm">Keep BragBox open on your phone. When a student has a breakthrough, a parent sends a kind email, or a lesson lands perfectly, take 30 seconds to log it. Your end-of-year self will have a goldmine of evidence.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Metrics Examples Section */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Key Metrics Teachers Should Track</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Student Achievement</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Growth percentiles (not just scores)</li>
                  <li>• Proficiency level improvements</li>
                  <li>• Assignment completion rates</li>
                  <li>• IEP goal achievement rates</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Classroom Climate</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Student engagement surveys</li>
                  <li>• Behavior referral trends</li>
                  <li>• Attendance rates</li>
                  <li>• Student feedback responses</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Professional Growth</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• PD hours completed</li>
                  <li>• Certifications earned</li>
                  <li>• Curriculum developed</li>
                  <li>• Strategies implemented</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Community Impact</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Parent conference attendance</li>
                  <li>• Communication engagement rates</li>
                  <li>• Extracurricular participation</li>
                  <li>• Colleague mentorship</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What should teachers include in their performance review?</h3>
                <p className="text-gray-300">Include student achievement data (test scores, growth metrics), classroom management successes, curriculum development, parent engagement initiatives, professional development completed, and extracurricular contributions. Quantify with specific numbers: "Improved class average reading scores from 72% to 89% proficiency."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How do teachers measure their impact for evaluations?</h3>
                <p className="text-gray-300">Track student growth percentiles, standardized test improvements, assignment completion rates, behavior referral reductions, parent satisfaction surveys, and student engagement metrics. Also document differentiated instruction successes and students who exceeded expectations.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What achievements beyond test scores should teachers document?</h3>
                <p className="text-gray-300">Document club sponsorships, committee leadership, curriculum writing, peer mentoring, innovative teaching methods, technology integration, student awards won, parent communication improvements, and community partnerships developed. These show impact beyond standardized assessments.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How often should teachers track their accomplishments?</h3>
                <p className="text-gray-300">Weekly tracking is ideal. Note student breakthroughs, successful lessons, parent feedback, and professional development as they happen. Use a tool like BragBox to capture wins quickly between classes. Waiting until evaluation season means forgetting impactful moments.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-[#C4A35A]/20 to-[#C4A35A]/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Your Impact Deserves Recognition</h2>
            <p className="text-xl text-gray-300 mb-8">
              BragBox helps teachers capture student breakthroughs, parent feedback, and classroom wins throughout the year.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/login?signup=true"
                className="inline-block bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Start free
              </Link>
              <Link to="/login" className="text-gray-300 underline underline-offset-4 hover:text-white transition-colors">
                Already have an account? Log in.
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                  <span className="text-[#1F2A44] font-bold text-sm">B</span>
                </div>
                <span className="text-white font-semibold">BragBox</span>
              </Link>
              <div className="flex gap-6 text-gray-400 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              </div>
              <p className="text-gray-500 text-sm">© 2026 BragBox. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Teachers;
